import React, { useState, useEffect, useRef } from 'react';
import { FiDelete, FiX } from 'react-icons/fi';
import './HandicapKeypadModal.css';

const HandicapKeypadModal = ({ isOpen, currentHandicap, onSave, onClose, saving, label, noDecimal, computePreview, previewLabel }) => {
    const [value, setValue] = useState('');
    const [isInitial, setIsInitial] = useState(true);
    const [pulse, setPulse] = useState(false);
    const displayRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setValue(currentHandicap != null ? String(currentHandicap) : '');
            setIsInitial(true);
        }
    }, [isOpen, currentHandicap]);

    // Trigger pulse animation on value change
    useEffect(() => {
        if (!isOpen) return;
        setPulse(true);
        const t = setTimeout(() => setPulse(false), 120);
        return () => clearTimeout(t);
    }, [value, isOpen]);

    if (!isOpen) return null;

    const handleKey = (key) => {
        if (noDecimal && key === '.') return;
        setValue(prev => {
            if (isInitial && key !== '-' && key !== '.') {
                setIsInitial(false);
                return key;
            }
            setIsInitial(false);
            if (key === '-') {
                return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
            }
            if (key === '.') {
                if (prev.includes('.')) return prev;
                if (prev === '' || prev === '-') return prev + '0.';
                return prev + '.';
            }
            const dotIndex = prev.indexOf('.');
            if (dotIndex !== -1 && prev.length - dotIndex > 1) return prev;
            const abs = prev.startsWith('-') ? prev.slice(1) : prev;
            if (abs === '0' && key !== '.') return prev.startsWith('-') ? '-' + key : key;
            return prev + key;
        });
    };

    const handleBackspace = () => {
        setIsInitial(false);
        setValue(prev => prev.slice(0, -1));
    };

    const handleSave = () => {
        const parsed = noDecimal ? parseInt(value, 10) : parseFloat(value);
        if (value === '' || isNaN(parsed)) return;
        onSave(parsed);
    };

    const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '0', noDecimal ? null : '.'];

    return (
        <div className="hkm-overlay" onClick={onClose}>
            <div className="hkm-modal" onClick={e => e.stopPropagation()}>
                <button className="hkm-close" onClick={onClose}><FiX size={16} /></button>

                <div className="hkm-header">
                    <span className="hkm-label">{label || 'Handicap Index'}</span>
                    <div className="hkm-divider" />
                    <div ref={displayRef} className={`hkm-display${pulse ? ' hkm-pulse' : ''}`}>
                        {value || '0'}
                    </div>
                    {computePreview && (() => {
                        const parsed = parseFloat(value);
                        const preview = value !== '' && !isNaN(parsed) ? computePreview(parsed) : null;
                        return (
                            <div className="hkm-preview">
                                {previewLabel || 'Playing Hcp'}
                                <span className="hkm-preview-val">{preview ?? '—'}</span>
                            </div>
                        );
                    })()}
                </div>

                <div className="hkm-keypad">
                    {keys.map((k, i) => k === null ? (
                        <button key="blank" className="hkm-key hkm-key--blank" disabled />
                    ) : (
                        <button
                            key={k}
                            className={`hkm-key ${k === '-' ? 'hkm-key--minus' : ''}`}
                            onClick={() => handleKey(k)}
                        >
                            {k === '-' ? '+/-' : k}
                        </button>
                    ))}
                </div>

                <div className="hkm-bottom-row">
                    <button className="hkm-key hkm-key--backspace" onClick={handleBackspace}>
                        <FiDelete size={20} />
                    </button>
                    <button
                        className="hkm-save"
                        onClick={handleSave}
                        disabled={saving || value === '' || isNaN(parseFloat(value))}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HandicapKeypadModal;
